import React, {FC, memo, useState} from "react";
import moment from "moment";
import {Button, Col, Form, Row} from "antd";
import {CheckOutlined} from "@ant-design/icons";
import {FormikProps} from "formik";
import {
    CreateInstallmentRateByCourseCreateInput,
    Scalars,
    UpdateInstallmentRateInput,
} from "../../generated/graphql";
import css from "./CourseCreate/CoursePricePreview.less";
import MoneyInput from "../../views/MoneyInput/MoneyInput";
import {PossibleFormikPropsType} from "./CourseCreate/CourseCreateForm";
import {updateTotalPrice} from "./helper/updateTotalPrice";
import DateInput from "../../views/DateInput/DateInput";

type EditRateModalProps = {
    rate: CreateInstallmentRateByCourseCreateInput;
    index: number;
    formikProps: FormikProps<PossibleFormikPropsType>;
    updateCurrentInstallmentRate: (
        rates: Array<CreateInstallmentRateByCourseCreateInput>,
    ) => void;
};

const EditRateModal: FC<EditRateModalProps> = ({
    rate,
    index,
    formikProps,
    updateCurrentInstallmentRate,
}) => {
    const [values, setValues] = useState(rate);
    const [installmentRates, setInstallmentRates] = useState<
        | Array<CreateInstallmentRateByCourseCreateInput>
        | Array<UpdateInstallmentRateInput>
        | null
        | undefined
    >(formikProps.values.installmentRates);

    const regenerateInstallmentPlan = (
        installmentAmount?: number,
        installmentDue?: Scalars["DateTime"],
    ) => {
        // update the installmentRate in formikProps.values.installmentRates
        const newInstallmentRates = formikProps.values.installmentRates?.map(
            (r) => {
                if (r.installmentDue === rate.installmentDue) {
                    return {
                        ...r,
                        installmentAmount,
                        installmentDue,
                    };
                }

                return r;
            },
        );

        setInstallmentRates(newInstallmentRates);
    };

    const saveRate = () => {
        // update the installmentRate in formikProps.values.installmentRates
        const newInstallmentRates = installmentRates?.map((r) => {
            if (r.installmentDue === rate.installmentDue) {
                return {
                    ...r,
                    installmentAmount: values.installmentAmount,
                };
            }

            return r;
        });

        const sortedNewInstallmentRates = newInstallmentRates?.sort(
            (a, b) =>
                new Date(a.installmentDue).getTime() -
                new Date(b.installmentDue).getTime(),
        );

        if (sortedNewInstallmentRates) {
            // update the installmentRate in formikProps.values.installmentRates
            formikProps.setFieldValue(
                "installmentRates",
                sortedNewInstallmentRates,
            );
            updateCurrentInstallmentRate(sortedNewInstallmentRates);
            updateTotalPrice(sortedNewInstallmentRates, formikProps);
        }
    };

    return (
        <div key={rate.installmentDue} className={css.monthlyPaymentEntry}>
            <div className={css.main}>
                <div className={css.month}>
                    <div className={css.form}>
                        <Row gutter={16}>
                            <Col span={1}>
                                <span className={css.num}>{`${index +
                                    1}.`}</span>
                            </Col>
                            <Col span={10}>
                                <Form.Item label="Zahlbar bis">
                                    <DateInput
                                        allowClear={false}
                                        displayFormat="DD.MM.YYYY"
                                        value={moment(
                                            values.installmentDue ?? undefined,
                                        )}
                                        onChange={(date, dateString) => {
                                            setValues({
                                                ...values,
                                                installmentDue: date,
                                            });
                                            regenerateInstallmentPlan(
                                                values.installmentAmount ?? 0,
                                                date,
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <Form.Item label="Betrag">
                                    <MoneyInput
                                        style={{width: "100%"}}
                                        value={values.installmentAmount}
                                        onChange={(e) => {
                                            setValues({
                                                ...values,
                                                installmentAmount: e,
                                            });
                                            regenerateInstallmentPlan(
                                                e,
                                                values.installmentDue,
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={2}>
                                <Form.Item label={" "} style={{paddingTop: 2}}>
                                    <Button
                                        type="primary"
                                        icon={<CheckOutlined />}
                                        onClick={() => saveRate()}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>
            <div className={css.sub}>
                {`Zahlbar bis ${moment(values.installmentDue).format(
                    "DD.MM.YY",
                )}`}
            </div>
        </div>
    );
};

export default memo(EditRateModal);
